import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  ClipboardList, Package, ShoppingCart, BarChart3, ChefHat, Settings, LifeBuoy,
  LayoutDashboard, RefreshCw, Store, User, CreditCard, Sparkles,
  ChevronUp, ChevronDown, ClipboardCheck, Send, FileText, Bell, Calendar, Megaphone, Tag,
} from 'lucide-react';
import PriceChangeTicker from './PriceChangeTicker';
import { markMessagesSeen, dismissMessage } from './DashboardMessages';
import { getCurrentUser } from '../utils/userDisplay';
import { NAV_TOOLS, findNavTarget } from '../utils/navTools';
import {
  vendorsMissingContact, summarizeSetup, deriveToolStatuses,
  deriveNeedsAttention, needsAttentionCount, buildDashboardAIContext,
} from '../utils/commandCenterStatus';
import { buildSetupCoachCard, setupCoachSessionKey } from '../utils/setupCoach';
import { createDashboardEventStream } from '../services/dashboardEventStream';
import { localTodayIso } from '../utils/dashboardEvents';

// AI Command Center — the develop-URL dashboard (public/dashboard-develop.html).
//
// Counter Intelligence is the focal point: ONE chat window, docked open in
// the middle of the page with suggestion pills directly beneath it, flanked
// by direct-action tool navigation (left rail on wide screens, grids below on
// smaller ones) and a live feed that streams the classic dashboard's channels
// as Slack-style messages — needs-attention cards on top, then price changes,
// counts, orders, invoices, upcoming schedules, and Countertop messages,
// pushed in realtime via dashboardEventStream. On wide screens the
// greeting/logo row sits above the chat column only, so the tool rail and
// live feed start level with the top of the chat window. Typed prompts submit
// into the global CounterIntelligenceDrawer via 'counter-intelligence:ask' so
// all chat behavior is the real assistant; the page also publishes a
// structured dashboard status (window.__accDashboardStatus) that the drawer
// forwards to the support answerer as DASHBOARD_CONTEXT.
//
// Navigation rules: every tool opens its destination directly. Only Admin
// opens a compact submenu (it genuinely holds several destinations) — and its
// items navigate directly. Chat links that map onto a tool "fly" there: the
// drawer closes, the tool pulses, then the page loads.

const COLORS = {
  dark: '#5A4A42',
  medium: '#8B7B73',
  muted: '#A89890',
  light: '#F5E2D0',
  border: '#E8DDD0',
  espresso: '#3D2518',
};

const TOOL_ICONS = {
  counts: ClipboardList,
  items: Package,
  purchasing: ShoppingCart,
  reports: BarChart3,
  recipes: ChefHat,
  admin: Settings,
  support: LifeBuoy,
  classic: LayoutDashboard,
  'switch-store': RefreshCw,
  'manage-stores': Store,
  account: User,
  billing: CreditCard,
};

const LAUNCH_DURATION_MS = 1200;

const timeGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

const firstNameOf = (user) => {
  const name = (user?.first_name || user?.user_metadata?.first_name || user?.displayName || '').trim();
  return name ? name.split(/\s+/)[0] : '';
};

const getStoreInfo = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem('currentStore') || 'null');
    if (parsed?.name) return { id: parsed.id || null, name: parsed.name };
  } catch (_) { /* fall through */ }
  return { id: null, name: localStorage.getItem('selectedStoreName') || '' };
};

const getStoreId = () => {
  const raw = localStorage.getItem('selectedStoreId') || localStorage.getItem('storeId');
  const parsed = parseInt(raw, 10);
  if (Number.isFinite(parsed)) return parsed;
  const fromStore = getStoreInfo().id;
  return fromStore ? parseInt(fromStore, 10) : null;
};

// ── Data loading ────────────────────────────────────────────────────────────
// The snapshot loader + live feed now live in services/dashboardEventStream.js
// (the page subscribes instead of querying directly).

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

const tickerEntriesFrom = (priceRows) => {
  const latestByItem = new Map();
  for (const row of priceRows || []) {
    const key = row.inventory_item_id || row.id;
    if (!latestByItem.has(key)) latestByItem.set(key, row);
    if (latestByItem.size >= 20) break;
  }
  return Array.from(latestByItem.values()).map((row) => ({
    name: row.new_name || row.old_name || '(unnamed)',
    vendor: row.invoice_vendor || '',
    unit: '',
    oldPrice: parseFloat(row.old_case_price) || 0,
    newPrice: parseFloat(row.new_case_price) || 0,
    date: row.changed_at || null,
  }));
};

// ── Stylesheet ──────────────────────────────────────────────────────────────
// Real CSS (not inline styles) so hover / active / focus-visible states and
// media queries behave like application controls. Injected once.

const STYLES = `
.acc-root { min-height: 100dvh; display: flex; flex-direction: column; color: ${COLORS.dark};
  font-family: 'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
.acc-shell { width: min(96vw, 1500px); margin: 0 auto; flex: 1; display: flex; flex-direction: column; box-sizing: border-box; }

/* Header — the Countertop logo lives in the hero now (see .acc-hero-logo);
   the header row only carries the store / account chips, pinned right. */
.acc-header { display: flex; align-items: center; justify-content: flex-end; gap: 12px;
  padding: max(14px, env(safe-area-inset-top, 0px)) 4px 10px; }
.acc-header-right { display: flex; align-items: center; gap: 14px; font-size: 0.82rem; color: ${COLORS.medium}; }
.acc-header-chip { display: inline-flex; align-items: center; gap: 6px; background: #fff;
  border: 1px solid ${COLORS.border}; border-radius: 999px; padding: 7px 13px; font-weight: 600; color: ${COLORS.dark}; }
.acc-header-email { max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* Layout — wide screens: the greeting/logo row occupies the chat column only
   (grid area "hero"), so the tool rail ("rail") and live feed ("side") start
   level with the top of the chat window ("chat") and grow with the page. */
.acc-grid { display: grid; grid-template-columns: 252px minmax(0, 1fr) 352px; grid-template-rows: auto 1fr;
  grid-template-areas: ". hero ." "rail chat side"; gap: 22px; align-items: start; flex: 1; padding-bottom: 14px; }
.acc-herohead { grid-area: hero; }
.acc-grid > .acc-rail { grid-area: rail; }
.acc-grid > main { grid-area: chat; display: flex; flex-direction: column; justify-content: flex-start; min-height: min(calc(100dvh - 170px), 760px); }
.acc-grid > aside { grid-area: side; }
.acc-stack { display: flex; flex-direction: column; gap: 20px; flex: 1; padding-bottom: 14px; }
.acc-chatblock { width: 100%; display: flex; flex-direction: column; align-items: center; }

/* Tool controls */
.acc-rail { display: flex; flex-direction: column; gap: 9px; }
.acc-tool { display: flex; align-items: center; gap: 12px; text-align: left; text-decoration: none;
  background: #fff; color: ${COLORS.dark}; border: 1.5px solid ${COLORS.border}; border-radius: 14px;
  padding: 11px 14px; min-height: 56px; box-sizing: border-box; cursor: pointer; width: 100%;
  box-shadow: 0 1px 3px rgba(45,35,28,0.06); transition: transform .14s ease, box-shadow .14s ease, border-color .14s ease;
  font-family: inherit; -webkit-tap-highlight-color: transparent; }
.acc-tool:hover { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(45,35,28,0.14); border-color: #D8C9BC; }
.acc-tool:active { transform: translateY(0); box-shadow: 0 1px 3px rgba(45,35,28,0.08); }
.acc-tool:focus-visible { outline: 3px solid #2E5E86; outline-offset: 2px; }
.acc-tool-icon { flex: 0 0 auto; width: 40px; height: 40px; border-radius: 11px; display: flex; align-items: center;
  justify-content: center; background: #F7EFE6; color: #7A5A42; }
.acc-tool-icon svg { width: 21px; height: 21px; }
.acc-tool-text { min-width: 0; display: flex; flex-direction: column; gap: 1px; }
.acc-tool-label { font-family: 'Montserrat', sans-serif; font-weight: 700; font-size: 0.99rem; line-height: 1.25; }
.acc-tool-status { font-size: 0.8rem; color: ${COLORS.medium}; line-height: 1.3; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.acc-tool-status.acc-hot { color: #9A4F32; font-weight: 600; }
.acc-tool.acc-card { flex-direction: column; align-items: flex-start; justify-content: center; gap: 8px;
  min-height: 96px; padding: 14px 15px; }
.acc-tool.acc-card .acc-tool-status { white-space: normal; }
.acc-tool.acc-secondary { background: #FCF9F5; }
.acc-tool.acc-launching { border-color: ${COLORS.espresso}; animation: accLaunchPulse 0.8s ease-out infinite; }
.acc-classic-link { display: inline-flex; align-items: center; gap: 7px; margin: 6px 2px 0; padding: 10px 6px;
  color: ${COLORS.medium}; font-size: 0.84rem; font-weight: 600; text-decoration: none; border-radius: 8px; }
.acc-classic-link:hover { color: ${COLORS.dark}; }
.acc-classic-link:focus-visible { outline: 3px solid #2E5E86; outline-offset: 2px; }
.acc-toolgrid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; }
@media (max-width: 859px) { .acc-toolgrid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
@media (max-width: 619px) { .acc-toolgrid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; } }

/* Admin submenu */
.acc-adminwrap { position: relative; }
.acc-menu { position: absolute; z-index: 90; min-width: 226px; background: #fff; border: 1.5px solid ${COLORS.border};
  border-radius: 14px; box-shadow: 0 16px 40px rgba(45,35,28,0.22); padding: 7px; top: calc(100% + 6px); left: 0; }
.acc-menu.acc-menu-side { top: 0; left: calc(100% + 8px); }
.acc-menu.acc-menu-right { left: auto; right: 0; }
.acc-menu-item { display: flex; align-items: center; gap: 10px; padding: 11px 12px; min-height: 44px; box-sizing: border-box;
  border-radius: 9px; text-decoration: none; color: ${COLORS.dark}; font-weight: 600; font-size: 0.9rem; }
.acc-menu-item svg { width: 17px; height: 17px; color: #7A5A42; }
.acc-menu-item:hover, .acc-menu-item:focus-visible { background: #F7EFE6; outline: none; }
.acc-menu-item.acc-launching { background: ${COLORS.light}; border: 1px solid ${COLORS.espresso}; animation: accLaunchPulse 0.8s ease-out infinite; }

/* Hero / AI workspace */
.acc-hero { display: flex; flex-direction: column; align-items: center; text-align: center; padding: 12px 4px 4px; }
/* Countertop logo — enlarged and centered above the chat window. */
.acc-hero-logo { height: 68px; width: auto; display: block; margin-bottom: 16px; }
@media (max-width: 619px) { .acc-hero-logo { height: 52px; } }
.acc-hero h1 { font-family: 'Montserrat', sans-serif; font-weight: 800; margin: 12px 0 4px;
  font-size: clamp(1.55rem, 1.1rem + 1.6vw, 2.1rem); }
.acc-hero-sub { font-size: 0.95rem; color: ${COLORS.medium}; margin: 0 0 18px; }
/* The single chat window lives here — the Counter Intelligence drawer is
   adopted into this slot and sized by the page's body.ai-home rules. */
.acc-chat-slot { width: 100%; flex: none; }
/* Suggestion pills, directly beneath the chat window. */
.acc-chips { width: min(100%, 900px); display: grid; grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 10px; margin-top: 14px; }
.acc-chip { display: flex; align-items: center; gap: 9px; text-align: left; background: rgba(255,255,255,0.9);
  border: 1.5px solid ${COLORS.border}; border-radius: 13px; padding: 12px 14px; min-height: 48px; box-sizing: border-box;
  color: ${COLORS.dark}; font-family: inherit; font-size: 0.9rem; font-weight: 600; cursor: pointer;
  transition: transform .14s ease, box-shadow .14s ease; -webkit-tap-highlight-color: transparent; }
.acc-chip svg { width: 16px; height: 16px; color: #B08968; flex: 0 0 auto; }
.acc-chip:hover { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(45,35,28,0.13); }
.acc-chip:active { transform: translateY(0); }
.acc-chip:focus-visible { outline: 3px solid #2E5E86; outline-offset: 2px; }
@media (max-width: 619px) {
  .acc-chips { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
  .acc-chip { font-size: 0.8rem; padding: 10px 11px; min-height: 44px; }
}

/* Live feed panel (replaces the tabbed Today panel): needs-attention cards
   on top, then the scrolling Slack-style event stream. On wide screens its
   max-height matches the docked chat window's own size formula (see the
   body.ai-home .ci-drawer rules in dashboard-develop.html), so the columns
   align top and bottom; the stream scrolls internally. */
.acc-feed { background: rgba(255,255,255,0.72); border: 1.5px solid ${COLORS.border}; border-radius: 16px;
  padding: 14px; box-sizing: border-box; display: flex; flex-direction: column; min-height: 0; }
.acc-grid .acc-feed { max-height: clamp(300px, calc(100dvh - 400px), 560px); position: sticky; top: 12px; }
.acc-feed-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 10px; flex: 0 0 auto; }
.acc-feed-heading { font-family: 'Montserrat', sans-serif; font-weight: 800; font-size: 1.16rem; display: inline-flex;
  align-items: center; gap: 8px; }
.acc-feed-scroll { overflow-y: auto; min-height: 0; flex: 1 1 auto; scrollbar-width: thin; }
.acc-feed-section { font-size: 0.72rem; letter-spacing: 0.08em; text-transform: uppercase; font-weight: 700;
  color: ${COLORS.muted}; margin: 12px 2px 6px; }
.acc-feed-item { display: flex; align-items: flex-start; gap: 10px; padding: 9px 8px; border-bottom: 1px solid #F2EBE3;
  text-decoration: none; color: inherit; border-radius: 9px; }
.acc-feed-item:hover { background: #FBF6F0; }
.acc-feed-icon { flex: 0 0 auto; width: 30px; height: 30px; border-radius: 9px; background: #F7EFE6; color: #7A5A42;
  display: flex; align-items: center; justify-content: center; margin-top: 1px; }
.acc-feed-icon svg { width: 15px; height: 15px; }
.acc-feed-main { min-width: 0; flex: 1; display: flex; flex-direction: column; gap: 2px; }
.acc-feed-title-line { font-size: 0.87rem; font-weight: 600; line-height: 1.35; color: ${COLORS.dark}; }
.acc-feed-text { font-size: 0.8rem; color: #6E5C52; line-height: 1.45; white-space: pre-wrap; }
.acc-feed-meta { font-size: 0.73rem; color: ${COLORS.muted}; }
.acc-feed-gotit { flex: 0 0 auto; align-self: center; border: 1px solid #D8C9BC; background: #fff; color: #6E5138;
  font-family: inherit; font-size: 0.74rem; font-weight: 700; padding: 6px 10px; border-radius: 999px; cursor: pointer; }
.acc-feed-gotit:hover { background: #F7EFE6; }
.acc-feed-gotit:focus-visible { outline: 3px solid #2E5E86; outline-offset: 1px; }
.acc-feed-fresh { animation: accFeedFresh 2.2s ease-out 1; }
@keyframes accFeedFresh {
  0% { background: #FDEBD7; }
  100% { background: transparent; }
}
.acc-badge { display: inline-flex; align-items: center; justify-content: center; min-width: 19px; height: 19px;
  border-radius: 999px; background: #B85C38; color: #fff; font-size: 0.68rem; font-weight: 800; padding: 0 5px; }
.acc-card-item { background: #fff; border: 1px solid ${COLORS.border}; border-radius: 13px; padding: 12px 13px; margin-bottom: 10px; }
.acc-card-item.acc-warn { border-left: 4px solid #C8880C; }
.acc-card-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.acc-card-title { font-family: 'Montserrat', sans-serif; font-weight: 700; font-size: 0.93rem; }
.acc-card-line { font-size: 0.85rem; color: #6E5C52; margin-top: 4px; line-height: 1.45; }
.acc-card-actions { display: flex; flex-wrap: wrap; gap: 7px; margin-top: 10px; }
.acc-btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; border-radius: 999px;
  font-family: inherit; font-weight: 700; font-size: 0.8rem; padding: 9px 14px; min-height: 36px; box-sizing: border-box;
  text-decoration: none; cursor: pointer; transition: transform .12s ease, box-shadow .12s ease; }
.acc-btn:hover { transform: translateY(-1px); }
.acc-btn:active { transform: translateY(0); }
.acc-btn:focus-visible { outline: 3px solid #2E5E86; outline-offset: 2px; }
.acc-btn-primary { background: ${COLORS.espresso}; color: #FBF7F1; border: 1px solid ${COLORS.espresso}; }
.acc-btn-quiet { background: #fff; color: #6E5138; border: 1px solid #D8C9BC; }
.acc-empty { font-size: 0.87rem; color: ${COLORS.medium}; padding: 14px 6px; text-align: center; line-height: 1.5; }

/* Bottom drawer (medium screens) */
.acc-drawer-bar { position: fixed; left: 0; right: 0; bottom: 0; z-index: 95; display: flex; align-items: center;
  justify-content: center; gap: 9px; background: #fff; border-top: 1.5px solid ${COLORS.border};
  box-shadow: 0 -6px 22px rgba(45,35,28,0.12); padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
  font-family: inherit; font-weight: 700; font-size: 0.9rem; color: ${COLORS.dark}; cursor: pointer; border-left: none;
  border-right: none; border-bottom: none; width: 100%; min-height: 48px; }
.acc-drawer-bar:focus-visible { outline: 3px solid #2E5E86; outline-offset: -3px; }
.acc-drawer-panel { position: fixed; left: 0; right: 0; bottom: 0; z-index: 96; background: #FDFAF6;
  border-top-left-radius: 18px; border-top-right-radius: 18px; box-shadow: 0 -18px 50px rgba(45,35,28,0.3);
  max-height: 58vh; overflow-y: auto; padding: 14px 16px calc(18px + env(safe-area-inset-bottom, 0px)); }
.acc-drawer-backdrop { position: fixed; inset: 0; z-index: 94; background: rgba(45,35,28,0.28); border: none; padding: 0; cursor: pointer; }

/* Ticker row */
.acc-ticker { padding: 4px 10px max(8px, env(safe-area-inset-bottom, 0px)); }

@keyframes accLaunchPulse {
  0%   { box-shadow: 0 0 0 0 rgba(61,37,24,0.45); }
  70%  { box-shadow: 0 0 0 18px rgba(61,37,24,0); }
  100% { box-shadow: 0 0 0 0 rgba(61,37,24,0); }
}

@media (prefers-reduced-motion: reduce) {
  .acc-tool, .acc-chip, .acc-btn, .acc-send { transition: none; }
  .acc-tool:hover, .acc-chip:hover, .acc-btn:hover, .acc-send:not(:disabled):hover { transform: none; }
  .acc-tool.acc-launching, .acc-menu-item.acc-launching { animation: none; }
  .acc-feed-fresh { animation: none; }
}
`;

let stylesInjected = false;
function injectStyles() {
  if (stylesInjected || typeof document === 'undefined') return;
  stylesInjected = true;
  const el = document.createElement('style');
  el.textContent = STYLES;
  document.head.appendChild(el);
}

// ── Small building blocks ───────────────────────────────────────────────────

function ToolIcon({ name }) {
  const Icon = TOOL_ICONS[name] || Package;
  return <span className="acc-tool-icon" aria-hidden="true"><Icon /></span>;
}

function ToolButton({ tool, variant, status, hot, launching, onClick, innerRef, ariaExpanded, ariaHasPopup }) {
  const className = [
    'acc-tool',
    variant === 'card' ? 'acc-card' : '',
    tool.secondary ? 'acc-secondary' : '',
    launching ? 'acc-launching' : '',
  ].filter(Boolean).join(' ');
  const body = (
    <>
      <ToolIcon name={tool.icon} />
      <span className="acc-tool-text">
        <span className="acc-tool-label">{tool.label}</span>
        {status && <span className={`acc-tool-status ${hot ? 'acc-hot' : ''}`}>{status}</span>}
      </span>
    </>
  );
  const aria = status ? `${tool.label} — ${status}` : tool.label;
  if (tool.href) {
    return (
      <a ref={innerRef} className={className} href={tool.href} aria-label={aria} onClick={onClick}>
        {body}
      </a>
    );
  }
  return (
    <button
      ref={innerRef}
      type="button"
      className={className}
      aria-label={aria}
      aria-haspopup={ariaHasPopup}
      aria-expanded={ariaExpanded}
      onClick={onClick}
    >
      {body}
    </button>
  );
}

// Admin: the one multi-destination tool. Opens a compact submenu whose items
// navigate directly. Escape closes and returns focus to the trigger; arrow
// keys move between items; clicking outside closes.
function AdminTool({ variant, status, sideMenu, launching, launchChildKey, forceOpen }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const isOpen = open || forceOpen;

  useEffect(() => {
    if (!isOpen) return undefined;
    const onDocDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDocDown);
    return () => document.removeEventListener('pointerdown', onDocDown);
  }, [isOpen]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape' && isOpen) {
      e.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && isOpen) {
      e.preventDefault();
      const items = Array.from(wrapRef.current?.querySelectorAll('[role="menuitem"]') || []);
      if (!items.length) return;
      const idx = items.indexOf(document.activeElement);
      const next = e.key === 'ArrowDown'
        ? items[(idx + 1) % items.length]
        : items[(idx - 1 + items.length) % items.length];
      next.focus();
    }
  };

  const adminTool = NAV_TOOLS.find((t) => t.key === 'admin');
  return (
    <div className="acc-adminwrap" ref={wrapRef} onKeyDown={onKeyDown} data-nav-tool="admin">
      <ToolButton
        tool={adminTool}
        variant={variant}
        status={status}
        launching={launching && !launchChildKey}
        innerRef={triggerRef}
        ariaHasPopup="menu"
        ariaExpanded={isOpen}
        onClick={() => setOpen((v) => !v)}
      />
      {isOpen && (
        <div className={`acc-menu ${sideMenu ? 'acc-menu-side' : ''}`} role="menu" aria-label="Admin">
          {adminTool.children.map((child) => {
            const Icon = TOOL_ICONS[child.icon] || Settings;
            return (
              <a
                key={child.key}
                className={`acc-menu-item ${launching && launchChildKey === child.key ? 'acc-launching' : ''}`}
                href={child.href}
                role="menuitem"
              >
                <Icon aria-hidden="true" />
                <span>{child.label}</span>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

const formatRelTime = (ts) => {
  const diff = Date.now() - ts;
  if (diff < -60000) {
    // Scheduled/future entries (upcoming counts, vendor order days).
    const days = Math.round(-diff / 86400000);
    if (days <= 0) return 'today';
    if (days === 1) return 'tomorrow';
    if (days < 7) return `in ${days} days`;
    return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  const diffMins = Math.floor(diff / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  return `${Math.floor(diffHrs / 24)}d ago`;
};

function NeedsAttentionCards({ cards }) {
  if (!cards || cards.length === 0) {
    return <div className="acc-empty">You’re all caught up. Ask Counter Intelligence what to work on next.</div>;
  }
  return (
    <>
      {cards.map((card) => (
        <div key={card.key} className={`acc-card-item ${card.tone === 'warn' ? 'acc-warn' : ''}`}>
          <div className="acc-card-head">
            <span className="acc-card-title">{card.title}</span>
            {card.badge && <span className="acc-badge" aria-label={`${card.badge} to resolve`}>{card.badge}</span>}
          </div>
          {card.lines.map((line, i) => <div key={i} className="acc-card-line">{line}</div>)}
          {card.actions?.length > 0 && (
            <div className="acc-card-actions">
              {card.actions.map((action, i) => (
                <a key={action.label} className={`acc-btn ${i === 0 ? 'acc-btn-primary' : 'acc-btn-quiet'}`} href={action.href}>
                  {action.label}
                </a>
              ))}
            </div>
          )}
        </div>
      ))}
    </>
  );
}

const FEED_KIND_ICONS = {
  price_change: Tag,
  count_created: ClipboardList,
  count_started: ClipboardList,
  count_completed: ClipboardCheck,
  count_upcoming: Calendar,
  order_sent: Send,
  order_upcoming: ShoppingCart,
  invoice: FileText,
  message: Megaphone,
};

// One Slack-style feed row: kind icon, headline, optional detail, relative
// time. Realtime arrivals flash once (item.live, skipped for reduced motion
// via CSS). Dismissable rows (Countertop messages) carry "✓ Got it".
function FeedItem({ item, onDismiss }) {
  const Icon = FEED_KIND_ICONS[item.kind] || Bell;
  const ref = useRef(null);
  const animatedRef = useRef(false);

  useEffect(() => {
    if (!item.live || animatedRef.current || !ref.current) return undefined;
    animatedRef.current = true;
    const el = ref.current;
    el.classList.add('acc-feed-fresh');
    const timer = setTimeout(() => el.classList.remove('acc-feed-fresh'), 2300);
    return () => clearTimeout(timer);
  }, [item.live]);

  const inner = (
    <>
      <span className="acc-feed-icon" aria-hidden="true"><Icon /></span>
      <span className="acc-feed-main">
        <span className="acc-feed-title-line">{item.title}</span>
        {item.body && <span className="acc-feed-text">{item.body}</span>}
        <span className="acc-feed-meta">{formatRelTime(item.ts)}</span>
      </span>
      {item.dismissable && onDismiss && (
        <button
          type="button"
          className="acc-feed-gotit"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDismiss(item); }}
        >
          ✓ Got it
        </button>
      )}
    </>
  );
  if (item.href) {
    return <a ref={ref} className="acc-feed-item" href={item.href}>{inner}</a>;
  }
  return <div ref={ref} className="acc-feed-item">{inner}</div>;
}

// The right-rail panel: actionable state (needs-attention cards) on top, then
// the scrolling live stream of every dashboard channel ("Latest").
function FeedPanel({ cards, attentionCount, events, onDismissMessage }) {
  return (
    <section className="acc-feed" aria-label="Today — live feed">
      <div className="acc-feed-head">
        <span className="acc-feed-heading">
          Today
          {attentionCount > 0 && (
            <span className="acc-badge" aria-label={`${attentionCount} to resolve`}>{attentionCount}</span>
          )}
        </span>
      </div>
      <div className="acc-feed-scroll">
        <NeedsAttentionCards cards={cards} />
        <div className="acc-feed-section">Latest</div>
        {(!events || events.length === 0) ? (
          <div className="acc-empty">
            No activity yet — price changes, counts, orders, and messages will stream in here.
          </div>
        ) : (
          events.map((item) => <FeedItem key={item.id} item={item} onDismiss={onDismissMessage} />)
        )}
      </div>
    </section>
  );
}

// ── The command center ──────────────────────────────────────────────────────

const breakpointFor = (w) => {
  if (w >= 1200) return 'xl';
  if (w >= 860) return 'md';
  if (w >= 620) return 'sm';
  return 'xs';
};

const AICommandCenter = () => {
  const [bp, setBp] = useState(() => breakpointFor(typeof window !== 'undefined' ? window.innerWidth : 1400));
  const [data, setData] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [launch, setLaunch] = useState(null); // { nodeKey, childKey, href }
  const launchRef = useRef(null);
  // The single chat window: the global drawer's DOM root is adopted into this
  // slot and docked permanently open, so its own input is the page's only
  // prompt (no popup, no duplicate hero box, one fixed size).
  const chatSlotRef = useRef(null);
  // Hold the adopted root ourselves. Crossing a breakpoint swaps the entire
  // grid/stack subtree, which detaches this root along with the old slot —
  // and a detached node is no longer findable by id, so without this handle
  // the chat window would be destroyed for good by a window resize.
  const ciRootRef = useRef(null);
  // What has focus inside the chat right now (null when focus is elsewhere).
  // On a breakpoint change React detaches the chat before the re-home runs,
  // by which point document.activeElement is already <body> — so the caret
  // can only be put back from what was recorded here.
  const focusInChatRef = useRef(null);
  // Live feed: events pushed by the dashboard event stream (snapshot +
  // realtime), and the stream handle used for optimistic message dismissals.
  const [feedEvents, setFeedEvents] = useState([]);
  const streamRef = useRef(null);

  const currentUser = useMemo(() => getCurrentUser(), []);
  const store = useMemo(() => getStoreInfo(), []);
  const todayIso = useMemo(() => localTodayIso(), []);

  useEffect(() => {
    injectStyles();
    window.__aiCommandCenterActive = true;
    // Dock the chat: permanently open, no overlay / close button / Escape
    // dismissal. Set the flag before dispatching so a drawer that mounts
    // later picks it up from its initial state.
    window.__ciDocked = true;
    try { window.dispatchEvent(new CustomEvent('counter-intelligence:dock')); } catch (_) { /* drawer not mounted yet */ }
    return () => {
      delete window.__aiCommandCenterActive;
      delete window.__accDashboardStatus;
      delete window.__ciDocked;
      try { window.dispatchEvent(new CustomEvent('counter-intelligence:undock')); } catch (_) { /* non-fatal */ }
    };
  }, []);

  useEffect(() => {
    const onResize = () => setBp(breakpointFor(window.innerWidth));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const onFocusIn = (e) => {
      const root = ciRootRef.current;
      focusInChatRef.current = root && root.contains(e.target) ? e.target : null;
    };
    document.addEventListener('focusin', onFocusIn);
    return () => document.removeEventListener('focusin', onFocusIn);
  }, []);

  // Adopt the drawer into the center workspace so the conversation lives in
  // the page, not a popup — page CSS (body.ai-home .ci-drawer) renders it as
  // a fixed-size card. Moving the node keeps the drawer's React state (state
  // lives in the component, not the DOM), so the conversation survives.
  const adoptChatWindow = useCallback(() => {
    const slot = chatSlotRef.current;
    const root = ciRootRef.current || document.getElementById('counter-intelligence-root');
    if (!slot || !root) return;
    ciRootRef.current = root;
    if (root.parentElement === slot) return;

    // Re-parenting a node resets the scroll position of its scrollable
    // descendants and blurs whatever was focused inside it. Unhandled, a
    // restored conversation opens at its OLDEST message and a resize drops
    // the caret mid-sentence, so carry both across the move.
    const list = root.querySelector('.ci-list');
    const focused = (root.contains(document.activeElement) ? document.activeElement : null)
      || focusInChatRef.current;
    // A detached node measures as all-zeros (React has already torn down the
    // old layout by the time this runs on a breakpoint change), which reads
    // the same as "at the bottom" — the right default for a chat either way.
    const previousTop = list ? list.scrollTop : 0;
    const keepAtBottom = list ? (list.scrollHeight - list.clientHeight - list.scrollTop) <= 4 : false;

    slot.appendChild(root);

    if (list && keepAtBottom) {
      // Pin now, then again once layout settles: a restored transcript is
      // measured before the web fonts land, and the reflow they cause would
      // otherwise leave the newest message below the fold. Each re-pin bails
      // if the position changed since the last one — that only happens when
      // the reader scrolls back through history, and they keep their place.
      let pinnedTop = -1;
      const pinToBottom = () => {
        if (pinnedTop >= 0 && list.scrollTop !== pinnedTop) return;
        list.scrollTop = list.scrollHeight;
        pinnedTop = list.scrollTop;
      };
      pinToBottom();
      requestAnimationFrame(pinToBottom);
      setTimeout(pinToBottom, 300);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(pinToBottom).catch(() => {});
    } else if (list) {
      list.scrollTop = previousTop;
    }
    if (focused && root.contains(focused) && typeof focused.focus === 'function') {
      focused.focus({ preventScroll: true });
    }
  }, []);

  // Re-home the window after EVERY render, not just on mount: a breakpoint
  // change re-creates the layout subtree (grid ↔ stack) and detaches the
  // adopted root, which would otherwise leave the page with no chat at all.
  // A layout effect puts it back before the browser paints, so nothing blinks.
  useLayoutEffect(adoptChatWindow);

  useEffect(() => {
    adoptChatWindow();
    // The drawer may mount after this component (script order).
    window.addEventListener('counter-intelligence:ready', adoptChatWindow);
    return () => {
      window.removeEventListener('counter-intelligence:ready', adoptChatWindow);
      const root = ciRootRef.current || document.getElementById('counter-intelligence-root');
      if (root && document.body && root.parentElement !== document.body) document.body.appendChild(root);
    };
  }, [adoptChatWindow]);

  // Load everything the page needs through the dashboard event stream, then
  // derive + publish the structured status Counter Intelligence receives as
  // DASHBOARD_CONTEXT. The stream keeps pushing after the first snapshot:
  // realtime row changes land in the feed instantly, and each one schedules a
  // debounced reload that also refreshes the needs-attention cards and tool
  // statuses.
  useEffect(() => {
    const storeId = getStoreId();
    if (!storeId) { setData({}); return undefined; }
    let cancelled = false;
    const stream = createDashboardEventStream({ storeId });
    streamRef.current = stream;
    const unsubscribe = stream.subscribe((events, snapshot) => {
      if (cancelled) return;
      setFeedEvents(events);
      if (snapshot) setData(snapshot);
    });
    stream.start().catch(() => { if (!cancelled) setData({}); });
    return () => {
      cancelled = true;
      unsubscribe();
      stream.stop();
      streamRef.current = null;
    };
  }, []);

  // Countertop messages in the feed mark themselves seen (and clear the
  // sidebar badge) exactly like the classic dashboard's message card.
  useEffect(() => {
    if (data?.messages?.length) markMessagesSeen(data.messages).catch(() => {});
  }, [data?.messages]);

  // "✓ Got it" on a feed message: optimistic remove; on failure reload so the
  // still-unread message comes back.
  const dismissFeedMessage = useCallback((item) => {
    const stream = streamRef.current;
    if (!stream || !item?.messageId) return;
    stream.drop(item.id);
    dismissMessage(item.messageId).catch(() => { stream.start().catch(() => {}); });
  }, []);

  const derived = useMemo(() => {
    const d = data || {};
    const setup = summarizeSetup(d.setupPayload);
    const vendorsMissing = vendorsMissingContact(d.vendors);
    const priceChanges7d = (d.priceRows || [])
      .filter((r) => (Date.parse(r.changed_at || '') || 0) >= Date.now() - SEVEN_DAYS_MS).length;
    const statuses = deriveToolStatuses({
      sessions: d.sessions || [],
      todayIso,
      itemCount: d.itemCount ?? null,
      actionablePOs: d.actionablePOs ?? 0,
      vendorsMissingContactCount: vendorsMissing.length,
    });
    const cards = deriveNeedsAttention({
      setup,
      vendorsMissing,
      sessions: d.sessions || [],
      todayIso,
      actionablePOs: d.actionablePOs ?? 0,
      priceChanges7d,
      invoices30d: (d.invoiceHistory || []).length,
    });
    const aiContext = buildDashboardAIContext({
      setup,
      vendorsMissing,
      sessions: d.sessions || [],
      todayIso,
      actionablePOs: d.actionablePOs ?? null,
      itemCount: d.itemCount ?? null,
      priceChanges7d,
      invoices30d: (d.invoiceHistory || []).length,
      messagesCount: (d.messages || []).length,
    });
    return {
      setup,
      statuses,
      cards,
      attentionCount: needsAttentionCount(cards),
      aiContext,
      ticker: tickerEntriesFrom(d.priceRows),
    };
  }, [data, todayIso]);

  // Publish the structured status for the drawer's support calls.
  useEffect(() => {
    if (data) window.__accDashboardStatus = derived.aiContext;
  }, [data, derived]);

  // Setup coaching: on sign-in (once per browser session, per store) the chat
  // opens with the setup-progress coach card — pills for progress, the
  // remaining steps with links, and chips into the assistant's own flows.
  // buildSetupCoachCard returns null once setup is complete, so the coaching
  // stops on its own.
  useEffect(() => {
    if (!data?.setupPayload) return undefined;
    const card = buildSetupCoachCard(data.setupPayload);
    if (!card) return undefined;
    const key = setupCoachSessionKey(getStoreId());
    try {
      if (sessionStorage.getItem(key)) return undefined;
      sessionStorage.setItem(key, '1');
    } catch (_) { /* storage unavailable — still coach this load */ }
    const timer = setTimeout(() => {
      try {
        window.dispatchEvent(new CustomEvent('counter-intelligence:setup-coach', { detail: { card } }));
      } catch (_) { /* drawer not mounted */ }
    }, 900);
    return () => clearTimeout(timer);
  }, [data]);

  const sendToCounterIntelligence = useCallback((text) => {
    const trimmed = (text || '').trim();
    if (!trimmed) return;
    try {
      window.dispatchEvent(new CustomEvent('counter-intelligence:ask', { detail: { text: trimmed } }));
    } catch (_) { /* drawer not mounted */ }
  }, []);

  // Fly-to: a chat link that maps onto a tool closes the chat, pulses the
  // tool (opening Admin's submenu when the destination is one of its items),
  // then loads the page. Reduced-motion users navigate immediately.
  const launchTo = useCallback((href, target) => {
    if (!href || launchRef.current) return;
    const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) { window.location.assign(href); return; }
    launchRef.current = { ...target, href };
    try { window.dispatchEvent(new CustomEvent('counter-intelligence:close')); } catch (_) { /* drawer optional */ }
    setLaunch({ ...target, href });
    setTimeout(() => { window.location.assign(href); }, LAUNCH_DURATION_MS);
  }, []);

  useEffect(() => {
    const onClickCapture = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = e.target.closest && e.target.closest('a[href]');
      if (!anchor || !anchor.closest('#ci-drawer-panel')) return;
      const href = anchor.getAttribute('href');
      const target = findNavTarget(href);
      if (!target) return;
      e.preventDefault();
      e.stopPropagation();
      launchTo(href, target);
    };
    document.addEventListener('click', onClickCapture, true);
    return () => document.removeEventListener('click', onClickCapture, true);
  }, [launchTo]);

  const greetName = firstNameOf(currentUser);
  const promptCards = useMemo(() => ([
    'What needs my attention today?',
    'What should I count today?',
    'Update prices from an invoice',
    'Which orders need attention?',
    'Show my latest food-cost changes',
    derived.setup ? 'Help me finish setup' : 'Where do I add new users?',
  ]), [derived.setup]);

  const toolsPrimary = NAV_TOOLS.filter((t) => t.primary);
  const toolsSecondary = NAV_TOOLS.filter((t) => !t.primary && !t.secondary);
  const classicTool = NAV_TOOLS.find((t) => t.secondary);
  const hotKeys = new Set();
  if ((data?.actionablePOs ?? 0) > 0) hotKeys.add('purchasing');
  if ((derived.statuses.counts || '').includes('in progress')) hotKeys.add('counts');

  const renderTool = (tool, variant) => {
    if (tool.children) {
      return (
        <AdminTool
          key={tool.key}
          variant={variant}
          status={derived.statuses[tool.key]}
          sideMenu={variant === 'row'}
          launching={launch?.nodeKey === 'admin'}
          launchChildKey={launch?.nodeKey === 'admin' ? launch.childKey : null}
          forceOpen={launch?.nodeKey === 'admin' && !!launch.childKey}
        />
      );
    }
    return (
      <div key={tool.key} data-nav-tool={tool.key}>
        <ToolButton
          tool={tool}
          variant={variant}
          status={derived.statuses[tool.key]}
          hot={hotKeys.has(tool.key)}
          launching={launch?.nodeKey === tool.key}
        />
      </div>
    );
  };

  // Greeting/logo row. On wide screens this sits in the grid's "hero" area —
  // above the chat column only — so the tool rail and live feed start level
  // with the top of the chat window.
  const heroHeader = (
    <div className="acc-hero">
      <img className="acc-hero-logo" src="/icons/Countertop-app_logo_text_only_counter.png" alt="Countertop" />
      <h1>{timeGreeting()}{greetName ? `, ${greetName}` : ''}</h1>
      <p className="acc-hero-sub">Ask Counter Intelligence about counts, orders, prices, recipes, or reports.</p>
    </div>
  );

  const heroChat = (
    <div className="acc-chatblock">
      {/* The one chat window on this page: the global Counter Intelligence
          root is adopted into this slot and docked open (see the effects
          above), so its own input is the only prompt — no duplicate hero box,
          no popup, and one fixed size whether it is idle or mid-conversation. */}
      <div ref={chatSlotRef} className="acc-chat-slot" />
      {/* Suggestion pills sit directly beneath the chat window. */}
      <div className="acc-chips">
        {promptCards.map((chip) => (
          <button key={chip} type="button" className="acc-chip" onClick={() => sendToCounterIntelligence(chip)}>
            <Sparkles aria-hidden="true" />
            <span>{chip}</span>
          </button>
        ))}
      </div>
    </div>
  );

  const feedPanel = (
    <FeedPanel
      cards={derived.cards}
      attentionCount={derived.attentionCount}
      events={feedEvents}
      onDismissMessage={dismissFeedMessage}
    />
  );

  const header = (
    <header className="acc-header">
      <div className="acc-header-right">
        {store.name && (
          <span className="acc-header-chip"><Store size={15} aria-hidden="true" />{store.name}</span>
        )}
        {currentUser?.email && bp !== 'xs' && (
          <span className="acc-header-email">{currentUser.email}</span>
        )}
      </div>
    </header>
  );

  const useDrawer = bp === 'md';

  return (
    <div className="acc-root">
      <div className="acc-shell" style={useDrawer ? { paddingBottom: '64px' } : undefined}>
        {header}

        {bp === 'xl' ? (
          <div className="acc-grid">
            <div className="acc-herohead">{heroHeader}</div>
            <nav className="acc-rail" aria-label="Main tools">
              {[...toolsPrimary, ...toolsSecondary].map((tool) => renderTool(tool, 'row'))}
              <a className="acc-classic-link" href={classicTool.href}>
                <LayoutDashboard size={15} aria-hidden="true" /> {classicTool.label}
              </a>
            </nav>
            <main>{heroChat}</main>
            <aside>{feedPanel}</aside>
          </div>
        ) : (
          <div className="acc-stack">
            <main>{heroHeader}{heroChat}</main>
            {bp === 'xs' ? (
              <>
                <nav className="acc-toolgrid" aria-label="Main tools">
                  {toolsPrimary.map((tool) => renderTool(tool, 'card'))}
                </nav>
                {feedPanel}
                <nav className="acc-toolgrid" aria-label="More tools">
                  {toolsSecondary.map((tool) => renderTool(tool, 'card'))}
                  {renderTool(classicTool, 'card')}
                </nav>
              </>
            ) : (
              <>
                <nav className="acc-toolgrid" aria-label="Main tools">
                  {[...toolsPrimary, ...toolsSecondary, classicTool].map((tool) => renderTool(tool, 'card'))}
                </nav>
                {!useDrawer && feedPanel}
              </>
            )}
          </div>
        )}

        {derived.ticker.length > 0 && (
          <div className="acc-ticker">
            <PriceChangeTicker entries={derived.ticker} />
          </div>
        )}
      </div>

      {useDrawer && !drawerOpen && (
        <button type="button" className="acc-drawer-bar" aria-expanded={false} onClick={() => setDrawerOpen(true)}>
          <span>Today{derived.attentionCount > 0 ? ` · ${derived.attentionCount} item${derived.attentionCount === 1 ? '' : 's'} need attention` : ''}</span>
          <ChevronUp size={17} aria-hidden="true" />
        </button>
      )}
      {useDrawer && drawerOpen && (
        <>
          <button type="button" className="acc-drawer-backdrop" aria-label="Close Today panel" onClick={() => setDrawerOpen(false)} />
          <div className="acc-drawer-panel" role="dialog" aria-label="Today">
            <button
              type="button"
              className="acc-drawer-bar"
              style={{ position: 'static', boxShadow: 'none', borderTop: 'none', padding: '2px 0 10px' }}
              aria-expanded
              onClick={() => setDrawerOpen(false)}
            >
              <span>Today</span>
              <ChevronDown size={17} aria-hidden="true" />
            </button>
            {feedPanel}
          </div>
        </>
      )}
    </div>
  );
};

export default AICommandCenter;
